import { Component, h, State, Host, Event, EventEmitter, Prop, Element } from '@stencil/core';
import { translations } from './translations';
import { sv, uuidv4, awaitTopbar } from '@/index';
import type { PnLanguages } from '@/index';

@Component({
  tag: 'pn-zipcode-search',
  styleUrl: 'pn-zipcode-search.scss',
})
export class PnZipcodeSearch {
  @Element() hostElement: HTMLElement;

  /** This language property will update when pnTopbar loads */
  @State() zipcodeId: string = `pn-zipcode-${uuidv4()}`;
  @State() validZipCode?: string;
  @State() loading: boolean = false;

  @State() showResult: boolean = false;
  @State() zipCodeSearchResult = {
    delivery: '',
    upcoming: '',
    city: '',
    postalCode: '',
  };

  @State() error: boolean = false;
  @State() errorMessage: string = '';

  /** The `language` property will be prioritized before the pnTopbar language. */
  @Prop() language?: PnLanguages = null;

  /** Event fired when search has result */
  @Event() searchsuccessful: EventEmitter<boolean>;

  async componentWillLoad() {
    if (this.language === null) return await awaitTopbar(this.hostElement);
  }

  getTranslation(key: string): string {
    return translations[key][this.language || sv];
  }

  handleSearch({ detail }) {
    this.validateZipCode(detail);

    if (this.error) {
      this.errorMessage = 'VALIDATION_ERROR_MESSAGE';
      return;
    }

    this.zipCodeSearch();
  }

  async zipCodeSearch() {
    this.loading = true;
    await fetch(`https://portal.postnord.com/api/sendoutarrival/closest?postalCode=${this.validZipCode}`)
      .then(response => response.json())
      .then(data => {
        this.showResult = true;
        this.zipCodeSearchResult = data;
        this.searchsuccessful.emit(true);
      })
      .catch(() => {
        this.error = true;
        this.errorMessage = 'SERVER_ERROR_MESSAGE';
        this.searchsuccessful.emit(false);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  validateZipCode(value: string): void {
    // Removes spaces and dashes
    const formatValue = value ? value.replace(/[ -]/gi, '') : '';

    const validRegex = /^\d{5}$/;
    const valid = validRegex.test(formatValue);

    this.error = !valid;

    if (valid) {
      this.validZipCode = formatValue;
    }
  }

  renderSearchResult() {
    if (!this.loading && !this.error && this.showResult) {
      return (
        <output class="search-results" htmlFor={this.zipcodeId}>
          <p class="text-row">
            <span>{this.getTranslation('CLOSEST_DELIVERY_DATE')}</span>
            <h3 class="delivery-date">{this.zipCodeSearchResult.delivery}</h3>
          </p>
          <p class="text-row">
            <span>{this.getTranslation('NEXT_DELIVERY_DATE')}</span>
            <h3 class="delivery-date">{this.zipCodeSearchResult.upcoming}</h3>
          </p>
          <p class="text-row">
            <span>{this.getTranslation('ZIP_CODE')}</span>
            <h3>
              {this.zipCodeSearchResult.postalCode}, {this.zipCodeSearchResult.city || 'N/A'}
            </h3>
          </p>
        </output>
      );
    }
  }

  renderErrorMessage() {
    if (this.error && !this.loading) {
      return (
        <p class="pn-error-text" role="alert">
          <small>{this.getTranslation(this.errorMessage)}</small>
        </p>
      );
    }
  }

  render() {
    return (
      <Host>
        <pn-search-field
          label={this.getTranslation('PLACEHOLDER_TEXT')}
          searchid={this.zipcodeId}
          loading={this.loading}
          button="none"
          placeholder={this.getTranslation('PLACEHOLDER_TEXT')}
          onSearch={e => this.handleSearch(e)}
        />
        {this.renderSearchResult()}
        {this.renderErrorMessage()}
      </Host>
    );
  }
}
